import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Mic, Send, MessageSquareReply } from "lucide-react";
import VoiceRecorder from "@/components/patient/VoiceRecorder";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const when = (iso) => new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long" });

function MessageItem({ m }) {
  return (
    <li className="rounded-2xl bg-sky-50/60 border border-sky-100 p-4" data-testid={`team-message-${m.id}`}>
      <p className="text-xs text-gray-500 mb-1">Envoyé le {when(m.created_at)}</p>
      {m.text && <p className="text-gray-800 whitespace-pre-line">{m.text}</p>}
      {m.audio_url && <audio controls preload="none" src={`${BACKEND}${m.audio_url}`} className="w-full h-10 mt-2" />}
      {m.reply ? (
        <div className="mt-3 rounded-xl bg-white border border-emerald-100 p-3" data-testid={`team-message-reply-${m.id}`}>
          <p className="text-xs font-semibold text-emerald-700 mb-1 flex items-center gap-1"><MessageSquareReply className="w-3.5 h-3.5" /> Réponse de {m.reply.by_name} · {when(m.reply.at)}</p>
          <p className="text-gray-700 whitespace-pre-line">{m.reply.text}</p>
        </div>
      ) : (
        <p className="text-xs text-gray-400 mt-2">{m.read ? "Lu par l'équipe" : "Pas encore lu"}</p>
      )}
    </li>
  );
}

// Petit message écrit ou vocal de la famille à l'équipe soignante.
export default function TeamMessages({ patientName }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const load = () => api.get("/family/team-messages").then((r) => setMsgs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const send = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("text", text.trim());
      await api.post("/family/team-messages", fd);
      setText(""); load();
      toast.success("Message envoyé à l'équipe soignante.");
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible."); }
    finally { setBusy(false); }
  };
  return (
    <section className="rounded-3xl bg-white border border-border/60 p-6 mb-8" data-testid="team-messages-section">
      <h3 className="font-serif-display text-2xl mb-1">Un mot pour l'équipe soignante</h3>
      <p className="text-gray-500 mb-4">Une question, une nouvelle à partager au sujet de {patientName} ? L'équipe peut vous répondre ici.</p>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} rows={3}
        placeholder="Votre message…" className="rounded-2xl text-base" data-testid="team-message-input" />
      <div className="flex flex-wrap gap-3 mt-3">
        <Button onClick={send} disabled={busy || !text.trim()} className="rounded-full bg-emerald-700 hover:bg-emerald-800" data-testid="team-message-send">
          <Send className="w-4 h-4 mr-2" /> Envoyer
        </Button>
        <Button variant="outline" onClick={() => setRecording(true)} className="rounded-full" data-testid="team-message-voice">
          <Mic className="w-4 h-4 mr-2" /> Message vocal
        </Button>
      </div>
      {recording && (
        <div className="mt-4">
          <VoiceRecorder postUrl="/family/team-messages" hideName title="Votre message vocal pour l'équipe"
            intro="Parlez tranquillement. Vous pourrez réécouter avant d'envoyer."
            onSaved={() => { setRecording(false); load(); toast.success("Message vocal envoyé à l'équipe soignante."); }}
            onClose={() => setRecording(false)} />
        </div>
      )}
      {msgs.length > 0 && <ul className="space-y-3 mt-6" data-testid="team-messages-list">{msgs.map((m) => <MessageItem key={m.id} m={m} />)}</ul>}
    </section>
  );
}
